/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Outlet, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import { type SpaceMenuKey, useSpaceStore } from '@coze-foundation/space-store';
import { useInitSpaceRole } from '@coze-common/auth-adapter';
import { useDestorySpace } from '@coze-common/auth';
import { I18n } from '@coze-arch/i18n';
import { Empty } from '@coze-arch/coze-design';
import { useRouteConfig } from '@coze-arch/bot-hooks';

const subMenuToPermissionKey = (
  subMenuKey?: string,
): SpaceMenuKey | undefined => {
  if (!subMenuKey) {
    return undefined;
  }
  if (subMenuKey.startsWith('prompt-engineering')) {
    return 'prompt';
  }
  if (subMenuKey.startsWith('evaluation')) {
    return 'evaluation';
  }
  const mapping: Partial<Record<string, SpaceMenuKey>> = {
    develop: 'agent',
    library: 'library',
    knowledge: 'knowledge',
    skills: 'skills',
    harness: 'harness',
    models: 'models',
    logs: 'logs',
    billing: 'billing',
    members: 'members',
    'operation-logs': 'operation_logs',
  };
  return mapping[subMenuKey];
};

const SpaceIdContainer = ({ spaceId }: { spaceId: string }) => {
  // When the space component is destroyed, empty the corresponding space data
  useDestorySpace(spaceId);

  // Initialize spatial permission data
  const isCompleted = useInitSpaceRole(spaceId);
  const { subMenuKey } = useRouteConfig();
  const permissionState = useSpaceStore(state => state.menuPermissions);
  const permissionLoading = useSpaceStore(
    state => state.menuPermissionsLoading,
  );
  const fetchMenuPermissions = useSpaceStore(
    state => state.fetchMenuPermissions,
  );
  const [permissionError, setPermissionError] = useState(false);

  useEffect(() => {
    setPermissionError(false);
    void fetchMenuPermissions(spaceId).catch(() => setPermissionError(true));
  }, [fetchMenuPermissions, spaceId]);

  if (!isCompleted || permissionLoading) {
    return null;
  }

  if (permissionError) {
    return (
      <Empty
        className="h-full w-full justify-center"
        title={I18n.t('space_permissions_load_failed')}
        description={I18n.t('space_permissions_load_failed_description')}
      />
    );
  }

  if (permissionState?.space_id !== spaceId) {
    return null;
  }

  const menuKey = subMenuToPermissionKey(
    typeof subMenuKey === 'string' ? subMenuKey : undefined,
  );
  if (menuKey && !permissionState.menus[menuKey]?.can_view) {
    return (
      <Empty
        className="h-full w-full justify-center"
        title={I18n.t('space_permissions_no_access')}
        description={I18n.t('space_permissions_no_access_description')}
      />
    );
  }

  // Wait for the workspace role before rendering routes that depend on its
  // effective menu permissions.
  return <Outlet />;
};

export const SpaceIdLayout = () => {
  const { space_id: spaceId } = useParams<{
    space_id: string;
  }>();

  return spaceId ? <SpaceIdContainer key={spaceId} spaceId={spaceId} /> : null;
};
